import React from 'react';
import BookingForm from './BookingForm';
import './CalendarDay.css';
import Header from './Header'; // Add this import
import { useLocation, useNavigate } from 'react-router-dom';
import { BS_BASE } from "./configs/api";
import { authHeader } from "./helpers/auth-header";

// CalendarDayView as a day view event picker
function CalendarDayView({ date, onTimeSelect, isSchool, soleUse, isSingleLesson }) {
  const [slots, setSlots] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    async function fetchSlots() {
      if (!date) return;
      setLoading(true);
      try {
        const formattedDate = date instanceof Date
          ? `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth()+1).toString().padStart(2, '0')}/${date.getFullYear()}`
          : date;
        // Helper to fetch and normalize slots for a given isSchool flag
        const fetchFor = async (flag) => {
          const response = await fetch(
            `${BS_BASE}/available-slots-by-date?date=${encodeURIComponent(formattedDate)}&isSchool=${flag}`,
            { headers: { Accept: 'application/json', ...authHeader() } }
          );
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

          const data = await response.json();

          const toHHMM = (s) => (typeof s === 'string' ? s.slice(0, 5) : '');
          const parseUsers = (u) => {
            if (!u) return [];
            if (Array.isArray(u)) return u.map(String).filter(Boolean);
            if (typeof u === 'string') return u.split(/[;,|]/).map(s => s.trim()).filter(Boolean);
            return [];
          };

          return (Array.isArray(data) ? data : (data?.slots ?? []))
            .map(it => ({
              start: toHHMM(it.startTime),
              end: toHHMM(it.endTime),
              allowedSoleUse: Boolean(it.allowedSoleUse),
              currentUsers: Number(it.currentUsers ?? 0),
              users: parseUsers(it.users ?? it.userNames ?? it.bookedUsers ?? it.bookedBy)
            }))
            .filter(s => s.start && s.end);
        };

        let mapped;
        if (isSingleLesson) {
          // Merge unbooked lesson + school slots
          const [lessonSlots, schoolSlots] = await Promise.all([
            fetchFor(false),
            fetchFor(true)
          ]);
          const merged = [...lessonSlots, ...schoolSlots]
            .filter(s => (s.currentUsers ?? 0) <= 0); // only unbooked
          mapped = merged;
        } else {
          mapped = await fetchFor(isSchool);
        }

        // When Sole Use is selected (school only), remove slots where allowedSoleUse is false OR currentUsers > 0
        const visible = (!isSingleLesson && soleUse)
          ? mapped.filter(s => s.allowedSoleUse && s.currentUsers <= 0)
          : mapped;

        // Dedup by start-end
        const unique = Array.from(new Map(visible.map(m => [`${m.start}-${m.end}`, m])).values());

        // Sort by start time (HH:mm) earliest to latest
        unique.sort((a, b) => a.start.localeCompare(b.start));

        setSlots(unique);
      } catch (error) {
        console.error('Failed to fetch slots:', error);
        setSlots([]);
      } finally {
        setLoading(false);
      }
    }
    fetchSlots();
  }, [date, isSchool, soleUse, isSingleLesson]); // include soleUse and isSingleLesson

  // Filter when Sole Use is on (also exclude any with currentUsers > 0)
  const displaySlots = React.useMemo(
    () => {
      // Single-lesson already filtered to unbooked above
      if (isSingleLesson) return slots;
      return soleUse ? slots.filter(s => s.allowedSoleUse && s.currentUsers <= 0) : slots;
    },
    [slots, soleUse, isSingleLesson]
  );

  return (
    <div className="calendar-day-view">
      {loading && <div style={{ padding: 8 }}>Loading slots…</div>}
      {!loading && displaySlots.length === 0 && (
        <div style={{ padding: 8 }}>No slots available for this date.</div>
      )}
      {!loading && displaySlots.length > 0 && (
        <div className="calendar-day-times-grid" style={{ marginTop: 0 }}>
          {displaySlots.map(slot => {
            const key = `${slot.start}-${slot.end}`;
            const busy = (slot.currentUsers ?? 0) > 0;
            const disabled = soleUse && !slot.allowedSoleUse;

            const titleParts = [`${slot.start}–${slot.end}`];
            if (busy) {
              const names = (slot.users?.length ? `: ${slot.users.join(', ')}` : '');
              titleParts.push(`${slot.currentUsers} booked${names}`);
            }
            if (disabled) titleParts.push('Sole Use not allowed');
            const title = titleParts.join(' · ');

            return (
              <button
                key={key}
                className={`calendar-day-time-btn${busy ? ' is-busy' : ''}`}
                tabIndex={disabled ? -1 : 0}
                disabled={disabled}
                onClick={() => !disabled && onTimeSelect(slot.start, slot.users)} // pass users with the time
                title={title}
                aria-label={title}
              >
                {slot.start}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CalendarDay() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedDate, isSchool, isSingleLesson } = location.state || {};

  // Sole Use toggle
  const [soleUse, setSoleUse] = React.useState(false);

  const userType = React.useMemo(() => {
    if (location?.state?.userType != null) return Number(location.state.userType);
    try {
      const stored = localStorage.getItem('currentUser');
      if (stored) {
        const cu = JSON.parse(stored);
        if (cu?.userType != null) return Number(cu.userType);
        if (cu?.type != null) return Number(cu.type);
        if (cu?.user?.userType != null) return Number(cu.user.userType);
      }
    } catch {}
    return undefined;
  }, [location.state]);

  // Accept users from CalendarDayView and forward to BookingForm
  const handleTimeSelect = (time, users) => {
    navigate('/bookingForm', {
      state: {
        selectedDate,
        selectedTime: time, // "HH:mm" or -1/-2/-3
        isSchool,
        soleUse,
        users, // pass users to BookingForm
        isSingleLesson,
        clientId: location.state?.clientId
      }
    });
  };

  return (
    <div className="bookingday-view">
      <div style={{ position: "relative" }}>
        <Header onBack={() => navigate(-1)} />
        <h2 style={{ marginTop: "5px", marginBottom: "18px" }}>
          {isSchool ? 'Book School' : 'Book Lesson'}
          <br />
          {selectedDate && (
            <span
              style={{
                fontSize: "1.5rem",
                fontWeight: 900,
                display: "block",
                marginTop: "-2px",
                color: "#4fa3c7"
              }}
            >
              {selectedDate.toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          )}
        </h2>

        {/* Sole Use toggle switch (not shown in single-lesson mode) */}
        {isSchool && !isSingleLesson && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, marginTop: -10 }}>
          <div className="soleuse-control">
            <span className="soleuse-caption">Sole Use</span>
            <label className={`soleuse-switch ${soleUse ? 'is-checked' : ''}`}>
              <input
                type="checkbox"
                checked={soleUse}
                onChange={(e) => setSoleUse(e.target.checked)}
                aria-label="Sole Use"
              />
              <span className="soleuse-track">
                <span className="soleuse-thumb" />
              </span>
            </label>
          </div>
          </div>
        )}

        {userType === 29 && isSchool && (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 4,
              justifyContent: "center",
              alignItems: "center",
              width: "100%"
            }}
          >
            <button className="calendar-day-time-btn" onClick={() => handleTimeSelect(-1)}>AM</button>
            <button className="calendar-day-time-btn" onClick={() => handleTimeSelect(-2)}>PM</button>
            <button className="calendar-day-time-btn" onClick={() => handleTimeSelect(-3)}>DAY</button>
          </div>
        )}

        <CalendarDayView
          date={selectedDate}
          onTimeSelect={handleTimeSelect}
          isSchool={isSchool}
          soleUse={soleUse} // pass to filter/render
          isSingleLesson={isSingleLesson}
        />
      </div>
    </div>
  );
}

export default CalendarDay;